import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import AOS from "aos";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import Hero from "../components/Hero"
import SEO from "../components/SEO";
import API from "../api";

const mdComponents = {
  p: ({ node, children }) => <p className="mb-4 text-gray-700">{children}</p>,
  ul: ({ node, children }) => <ul className="list-disc pl-6 mb-4">{children}</ul>,
  ol: ({ node, children }) => <ol className="list-decimal pl-6 mb-4">{children}</ol>,
  li: ({ node, children }) => <li className="mb-1">{children}</li>,
  h1: ({ node, children }) => <h1 className="text-2xl font-bold mb-3">{children}</h1>,
  h2: ({ node, children }) => <h2 className="text-xl font-semibold mb-2">{children}</h2>,
};

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const ArticleDetailsPage = () => {
  const { id } = useParams();

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  // Ensure the page scrolls to top whenever this page is navigated to or the id changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window?.scrollTo) {
        // instant jump to top to avoid flashing at previous scroll position
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      // fallback: simple assignment
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        // ignore in environments without window
      }
    }
  }, []);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // const apiUrl = 'http://localhost:4000';

  useEffect(() => {
    let mounted = true;
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        // Try endpoint for single article first
        const res = await API.get(`/articles/${id}`);
        const data = res.data && (res.data.article || res.data.data || res.data) ? (res.data.article || res.data.data || res.data) : null;
        if (mounted && data) {
          setArticle(data);
          return;
        }

        // Fallback: fetch all articles and find by id
        const allRes = await API.get('/articles');
        const allData = allRes.data && (Array.isArray(allRes.data) ? allRes.data : allRes.data.articles || allRes.data.data) ? (Array.isArray(allRes.data) ? allRes.data : allRes.data.articles || allRes.data.data) : [];
        const articleId = Number(id);
        const found = (Array.isArray(allData) ? allData : []).find((c) => Number(c.id) === articleId);
        if (mounted) setArticle(found || null);
      } catch (err) {
        console.error('Failed to load article', err);
        if (mounted) setError(err.message || 'Failed to load article');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchArticle();

    return () => {
      mounted = false;
    };
  }, [id]);

  const heroSlides = [
    {
      id: article ? article.id : "not-found",
      image: apiUrl + article?.image || "/assets/img/prog-1.jpg",
      title: article?.title || "Article",
      subtitle: article?.excerpt || "",
      buttonText: article ? "Read" : "Back",
      buttonLink: article ? "#" : "/articles",
    },
  ];

  const formattedDate = article && article.date ? new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  const articleUrl = typeof window !== 'undefined' ? window.location.href : `https://africaresearchuniversity.com/articles/${id}`;

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': article.title,
        'image': [ apiUrl + article.image ],
        'datePublished': article.date,
        'author': article.author || 'Africa Research University',
        'publisher': {
          '@type': 'Organization',
          'name': 'Africa Research University',
          'logo': { '@type': 'ImageObject', 'url': '/assets/img/arulogo.png' }
        },
        'description': article.excerpt || article.content?.slice(0, 160) || ''
      }
    : null;

  return (
    <>
      <SEO
        title={article ? `${article.title} - Africa Research University` : 'Article - ARU'}
        description={article?.excerpt || article?.content?.slice(0, 150)}
        image={article ? apiUrl + article.image : '/assets/img/arulogo.png'}
        url={articleUrl}
        jsonLd={articleSchema}
        breadcrumbs={[
          { name: 'Home', url: 'https://africaresearchuniversity.com' },
          { name: 'News & Events', url: 'https://africaresearchuniversity.com/articles' },
          { name: article?.title || 'Article', url: articleUrl }
        ]}
      />
    <section className="py-1">
      <Hero slides={heroSlides} interval={5000} height="280px" borderRadius="50px" hidden={true} />

      <div className="container mx-auto px-4 mt-8 grid md:grid-cols-3 gap-8" data-aos="fade-up">
        <main className="md:col-span-2">
          {article ? (
            <article className="bg-white rounded-lg shadow p-6">
              <h1 className="text-2xl font-bold mb-2">{article.title}</h1>
              <p className="text-sm text-green-700 font-medium mb-4">{article.category_name} • {formattedDate}</p>

              <section className="mb-6 prose prose-sm sm:prose lg:prose-lg">
                {article.content ? (
                  <div
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                ) : (
                  <p>No introduction available.</p>
                )}
              </section>
            </article>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-700">Article not found.</p>
              <Link to="/articles" className="inline-block mt-4 text-green-600 hover:underline">Back to articles</Link>
            </div>
          )}
        </main>

        <aside>
          {article && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <img loading="lazy" src={apiUrl + article.image} alt={article.title} className="rounded-md w-full object-cover" />
              <div>
                <h4 className="font-semibold">Published</h4>
                <p className="text-gray-700">{formattedDate}</p>
              </div>
              <div>
                <h4 className="font-semibold">Category</h4>
                <p className="text-gray-700">{article.category_name}</p>
              </div>
              <Link to="/articles" className="block text-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Browse articles</Link>
            </div>
          )}
        </aside>
      </div>
    </section>
    </>
  );
};

export default ArticleDetailsPage;
